/**
 * Wheel scroll chaining for split player panels (ported from shared SplitPanel).
 * When the panel cannot consume more wheel delta, scroll the nearest ancestor.
 */

function getWheelTargetElement(target: EventTarget | null): HTMLElement | null {
    if (target instanceof HTMLElement) return target;
    if (target instanceof Node && target.parentElement) return target.parentElement;
    return null;
}

function descendantChainCanConsumeVerticalWheel(
    start: HTMLElement,
    scrollHost: HTMLElement,
    deltaY: number
): boolean {
    let node: HTMLElement | null = start;
    while (node && node !== scrollHost) {
        const st = window.getComputedStyle(node);
        const oy = st.overflowY;
        if (
            (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
            node.scrollHeight > node.clientHeight + 1
        ) {
            const top = node.scrollTop;
            const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
            if ((deltaY < 0 && top > 0) || (deltaY > 0 && top < maxScroll - 1)) {
                return true;
            }
        }
        node = node.parentElement;
    }
    return false;
}

function findVerticalScrollParent(el: HTMLElement | null): HTMLElement | null {
    let node = el;
    while (node) {
        const st = window.getComputedStyle(node);
        const oy = st.overflowY;
        if (
            (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
            node.scrollHeight > node.clientHeight + 1
        ) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

export function attachWheelScrollChain(scrollHost: HTMLElement): () => void {
    const onWheel = (e: WheelEvent) => {
        const deltaY = e.deltaY;
        if (deltaY === 0) return;

        const start = getWheelTargetElement(e.target);
        if (!start || !scrollHost.contains(start)) return;

        if (descendantChainCanConsumeVerticalWheel(start, scrollHost, deltaY)) {
            return;
        }

        const sh = scrollHost.scrollHeight;
        const ch = scrollHost.clientHeight;
        const hasVerticalOverflow = sh > ch + 1;
        const top = scrollHost.scrollTop;
        const maxScroll = Math.max(0, sh - ch);

        const canConsumeWheel =
            hasVerticalOverflow &&
            ((deltaY < 0 && top > 0) || (deltaY > 0 && top < maxScroll - 1));

        if (canConsumeWheel) return;

        const outer = findVerticalScrollParent(scrollHost.parentElement);
        if (!outer) return;

        e.preventDefault();
        outer.scrollTop += deltaY;
    };

    scrollHost.addEventListener('wheel', onWheel, { passive: false });
    return () => scrollHost.removeEventListener('wheel', onWheel);
}
